import { useState } from 'react';
import {
  Color, Size, TIME_OFF_APPROVALS_PATH, fill, formatDateOnly, t, timeOffApprovePath, timeOffHoursText, timeOffRejectPath,
  type TimeOffApprovalItem, type TimeOffApprovalsAnswer, type TimeOffTeamDay,
} from '@bms2/shared';
import { Button, Textarea } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { FormFrame } from '../../components/forms/FormFrame';
import { useDateFormat } from '../../shell/ViewerZone';
import { post, useApi } from '../records/api';
import { line } from '../settings/SettingsShell';
import { timeOffSpan } from './RequestTimeOff';
import styles from './TimeApproval.module.css';

/** One requested day as the approver reads it (decision 0231): who else is off, approved and pending, and who is left. */
function teamDayLine(day: TimeOffTeamDay): string {
  if (day.off) return t('hr.timeOffApproval.dayOff');
  const parts = [
    day.approved.length > 0 ? fill(t('hr.timeOffApproval.off'), { names: day.approved.join(', ') }) : null,
    day.pending.length > 0 ? fill(t('hr.timeOffApproval.pendingOff'), { names: day.pending.join(', ') }) : null,
  ].filter((part): part is string => part !== null);
  return line(parts.length > 0 ? parts.join(' · ') : t('hr.timeOffApproval.nobodyOff'), '·', fill(t('hr.timeOffApproval.left'), { left: day.left, team: day.teamSize }));
}

/**
 * Time Off Requests on Time Awaiting Approval (piece 34; decisions 0231,
 * 13030; T3871): every request this viewer decides, as a manager above the job
 * or HR with the hire permission. 🔒 Each shows, for each requested day, the
 * team's approved and pending time off and the headcount left before it asks
 * for a yes; Reject asks the reason the person reads.
 */
export function TimeOffApprovals({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const format = useDateFormat();
  const read = useApi<TimeOffApprovalsAnswer>(TIME_OFF_APPROVALS_PATH, apiBaseUrl);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<TimeOffApprovalItem | null>(null);
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);

  const approve = async (item: TimeOffApprovalItem) => {
    setBusy(true);
    setError(null);
    setSaid(null);
    const answer = await post<{ requestId: string }>(timeOffApprovePath(item.request.id), {}, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    setSaid(fill(t('hr.timeOffApproval.approved'), { name: item.name }));
    read.reload();
  };
  const reject = async () => {
    if (!rejecting) return;
    const answer = await post<{ requestId: string }>(timeOffRejectPath(rejecting.request.id), { reason }, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    setSaid(fill(t('hr.timeOffApproval.rejected'), { name: rejecting.name }));
    setRejecting(null);
    read.reload();
  };

  return (
    <section className={styles.days} data-time-off-approvals="true" aria-label={t('hr.timeOffApproval.heading')}>
      <h2 className={styles.dayTitle}>{t('hr.timeOffApproval.heading')}</h2>
      <p className={styles.hint}>{t('hr.timeOffApproval.hint')}</p>
      {error ? <p className={styles.problem} role="alert" data-time-off-approval-error="true">{error}</p> : null}
      {said ? <p className={styles.hint} role="status" data-time-off-approval-said="true">{said}</p> : null}
      <AsyncStatePanel state={read.state} emptyAction={t('hr.timeOffApproval.empty')} onRetry={read.reload}>
        {(answer) => (
          <div className={styles.days}>
            {(answer.items ?? []).length === 0 ? <p className={styles.hint} data-time-off-approval-empty="true">{t('hr.timeOffApproval.empty')}</p> : null}
            {(answer.items ?? []).map((item) => (
              <section key={item.request.id} className={styles.day} data-time-off-approval={item.request.id}>
                <header className={styles.dayHead}>
                  <h3 className={styles.dayTitle}>{line(item.name, '·', item.request.planName, '·', timeOffSpan(item.request, format), '·', timeOffHoursText(item.request.hours))}</h3>
                  <span className={styles.actions}>
                    <span data-time-off-approve="true">
                      <Button size={Size.Small} color={Color.Good} text={[t('hr.timeOffApproval.approve')]} disabled={busy} actions={[() => void approve(item)]} />
                    </span>
                    <span data-time-off-reject="true">
                      <Button size={Size.Small} color={Color.Warning} text={[t('hr.timeOffApproval.reject')]} disabled={busy} actions={[() => { setReason(''); setRefusal(null); setRejecting(item); }]} />
                    </span>
                  </span>
                </header>
                <p className={styles.hint} data-time-off-approval-balance="true">
                  {fill(t('hr.timeOffApproval.balance'), { requestable: timeOffHoursText(Math.max(0, item.requestable)), after: timeOffHoursText(item.after) })}
                </p>
                {item.request.note ? <p className={styles.hint}>{item.request.note}</p> : null}
                {/* 🔒 Decision 0231: the team on each requested day, before the approver is asked for a yes. */}
                <ul className={styles.entries}>
                  {item.days.map((day) => (
                    <li key={day.day} className={styles.entry} data-time-off-team-day={day.day}>
                      <span>{formatDateOnly(`${day.day}T00:00:00.000Z`, format)}</span>
                      <span className={styles.hint}>{teamDayLine(day)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </AsyncStatePanel>
      {/* 🔨 Mounted only while open and keyed to the request, so a second request's window never shows the first one's reason. */}
      {rejecting ? (
        <FormFrame
          key={rejecting.request.id}
          host="window"
          open
          title={t('hr.timeOffApproval.rejectTitle')}
          subtitle={line(rejecting.name, '·', timeOffSpan(rejecting.request, format))}
          dataForm="time-off-reject"
          typed={reason !== ''}
          onCancel={() => setRejecting(null)}
          refusal={refusal}
          onDismissRefusal={() => setRefusal(null)}
          act={{ label: t('hr.timeOffApproval.reject'), onAct: () => reject(), disabled: reason.trim() === '', disabledReason: t('hr.timeOffApproval.reasonHint') }}
        >
          <p className={styles.hint}>{t('hr.timeOffApproval.reasonHint')}</p>
          <Textarea size={Size.Large} label={t('hr.timeOffApproval.reason')} value={reason} onChange={setReason} required name="timeOffRejectReason" />
        </FormFrame>
      ) : null}
    </section>
  );
}
